import { useRef } from 'react';
import { FlexCenter, FlexColumn } from '@elementary/common-ui/components';
import {
  DataContractConfigApi,
  DataContractFormValues,
} from '@elementary/common-ui/types';
import { CircularProgress } from '@mui/material';
import { UseFormReturn } from 'react-hook-form';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import useEnvPermission from '@elementary/common-ui/hooks/useEnvPermission';
import useSnackbar from 'shared/hooks/useSnackbar';
import useTestsPolling from 'shared/hooks/useTestsPolling';
import * as api from 'shared/services/api';
import useSidePanelParams from '@elementary/common-ui/hooks/useSidePanelParams';
import useDataContractTestConfiguration from '../../hooks/useDataContractTestConfiguration';
import { TestOverview } from '../../types';
import DataContractTestConfigurationFields from './DataContractTestConfigurationFields';

type Props = {
  testOverview: TestOverview;
};

const DataContractTestConfiguration = ({ testOverview }: Props) => {
  const testConfigRef = useRef<{
    methods: UseFormReturn<DataContractFormValues>;
  }>();

  const { openSnackbar } = useSnackbar();
  const { setPollingTestsIds } = useTestsPolling();
  const selectedEnvId = useSelectedEnv();
  const { onPanelConfigSaved } = useSidePanelParams();
  const hasPermission = useEnvPermission();
  const hasEditPermission = hasPermission('EDIT_TEST_CONFIGURATIONS');

  const {
    testConfig,
    updateConfig,
    refetchTestConfiguration,
    isFetching,
    isUpdating,
  } = useDataContractTestConfiguration(testOverview);

  const handleSaveChanges = () => {
    const values = testConfigRef.current?.methods.getValues();
    if (!values) return;

    const payload: DataContractConfigApi = {
      baseline_mode: values.baselineMode,
      baseline_columns:
        values.baselineMode === 'manual' ? values.baselineColumns : null,
      enforcement_level: values.enforcementLevel,
    };

    updateConfig(payload, {
      onSuccess: () => {
        onPanelConfigSaved();
        refetchTestConfiguration();
        setPollingTestsIds((prev) => new Set([...prev, testOverview.test.id]));
        openSnackbar({
          message: 'Test configuration saved, it will be applied shortly.',
          variant: 'success',
        });
      },
      onError: () => {
        openSnackbar({
          message: 'Failed to save test configuration.',
          variant: 'error',
        });
      },
    });
  };

  const handleCaptureCurrentSchema = async () => {
    if (!selectedEnvId) return null;
    try {
      const response = await api.fetchCurrentSchema(
        selectedEnvId,
        testOverview.test.id,
      );
      return response.columns;
    } catch {
      openSnackbar({
        message: 'Failed to capture current schema.',
        variant: 'error',
      });
      return null;
    }
  };

  const formConfig: DataContractFormValues | undefined = testConfig
    ? {
        baselineMode: 'manual',
        baselineColumns: testConfig.baselineColumns.map((col) => ({
          column_name: col.columnName,
          data_type: col.dataType,
          is_nullable: col.isNullable,
        })),
        enforcementLevel: testConfig.enforcementLevel,
      }
    : undefined;

  return (
    <FlexColumn sx={{ gap: 3 }}>
      {isFetching && (
        <FlexCenter sx={{ height: '100%', padding: '0 24px' }}>
          <CircularProgress size={32} />
        </FlexCenter>
      )}

      {!isFetching && formConfig && (
        <DataContractTestConfigurationFields
          ref={testConfigRef}
          config={formConfig}
          isLoading={isUpdating}
          onSave={handleSaveChanges}
          onCapture={handleCaptureCurrentSchema}
          readOnly={!hasEditPermission}
        />
      )}
    </FlexColumn>
  );
};

export default DataContractTestConfiguration;
